import { html } from "lit";
import type { SessionSnapshot } from "../chat/session-snapshot.ts";
import type { FleetProjectSummary, GatewayAgentRow, SessionsListResult } from "../types.ts";
import type { ChatSubagentCard } from "../ui-types.ts";
import { renderFleetChatDelegationMap } from "./fleet-chat-delegation-map.ts";
import { renderFleetSessionBar, type FleetSessionBarProps } from "./fleet-session-bar.ts";

export type FleetV2DirectoryTab = "map" | "sessions";

export type FleetV2AgentDirectoryProps = {
  activeTab: FleetV2DirectoryTab;
  onTabChange: (tab: FleetV2DirectoryTab) => void;
  connected: boolean;
  agents: GatewayAgentRow[];
  subagents: ChatSubagentCard[];
  /**
   * UX-PLAN Pillar D — when the parent has computed a `SessionSnapshot`,
   * we forward it to the delegation map so its children + empty-state
   * logic read from the same derived value as the chat thread.
   * Optional so legacy callers (tests, snapshot-free renders) still work.
   */
  sessionSnapshot?: SessionSnapshot;
  parentSessionKey: string;
  sessions: SessionsListResult | null;
  projects?: FleetProjectSummary[];
  onOpenAgentsTab?: (agentId: string) => void;
  onOpenSubagentSession?: (params: {
    childKey: string;
    parentKey: string;
    label: string;
    task?: string;
    runId?: string;
  }) => void;
  onOpenProjectSession?: (project: FleetProjectSummary) => void;
  onEditRoutes?: () => void;
  /**
   * Slot for the chat-transcript session navigator that used to live above
   * the transcript (arrow history, Reset Node, open tabs). When the active
   * tab is "sessions" we render this body; otherwise we hide it so the
   * Map panel is not polluted.
   *
   * Pass `null`/`undefined` when the parent view doesn't surface a session
   * navigator (e.g. operator preset without a chat tab).
   */
  sessionBar?: FleetSessionBarProps | null;
};

const FLEET_V2_DIRECTORY_TABS: Array<{ id: FleetV2DirectoryTab; label: string }> = [
  { id: "map", label: "Fleet map" },
  { id: "sessions", label: "Sessions" },
];

function renderMapTab(props: FleetV2AgentDirectoryProps) {
  return html`
    <div class="fleet-v2-directory__map">
      ${renderFleetChatDelegationMap({
        sessionKey: props.parentSessionKey,
        subagents: props.subagents,
        snapshot: props.sessionSnapshot,
        sessions: props.sessions,
        projects: props.projects,
        onOpenSubagentSession: props.onOpenSubagentSession,
        onOpenProjectSession: props.onOpenProjectSession,
        onEditRoutes: props.onEditRoutes,
      })}
    </div>
  `;
}

/**
 * V2 directory → Sessions tab. Hosts the chat-transcript session
 * navigator (arrow history, Reset Node, open tabs, session catalog) that
 * used to live as a sticky bar above the transcript.
 */
function renderSessionsTab(props: FleetV2AgentDirectoryProps) {
  const barProps = props.sessionBar ?? null;
  if (!barProps) {
    return html`
      <div class="fleet-v2-directory__sessions fleet-v2-directory__sessions--empty">
        <p class="muted">Session navigator is unavailable in this view.</p>
      </div>
    `;
  }
  // The session bar expects to be the full-width body of its container, so
  // we wrap it but let it own its layout (collapsed vs expanded).
  return html`
    <div class="fleet-v2-directory__sessions" data-testid="fleet-v2-directory-sessions">
      ${renderFleetSessionBar(barProps)}
    </div>
  `;
}

function renderActiveTab(props: FleetV2AgentDirectoryProps) {
  if (props.activeTab === "map") {
    return renderMapTab(props);
  }
  return renderSessionsTab(props);
}

export function renderFleetV2AgentDirectory(props: FleetV2AgentDirectoryProps) {
  const activeTab = props.activeTab;

  return html`
    <div class="fleet-v2-directory">
      <div class="fleet-v2-directory__tabs" role="tablist" aria-label="Agent directory views">
        ${FLEET_V2_DIRECTORY_TABS.map(
          (tab) => html`
            <button
              type="button"
              role="tab"
              class="fleet-v2-directory__tab ${activeTab === tab.id ? "fleet-v2-directory__tab--active" : ""}"
              aria-selected=${activeTab === tab.id}
              @click=${() => props.onTabChange(tab.id)}
            >
              ${tab.label}
            </button>
          `,
        )}
      </div>
      <div class="fleet-v2-directory__panel" role="tabpanel">${renderActiveTab(props)}</div>
    </div>
  `;
}
